import { Center, Loader } from "@mantine/core";
import { lazy, Suspense } from "react";

import { ClientOnly, Hero } from "@mountayaapp/markdoc/renderers";

import "./style.css";

// Redoc bundles a large OpenAPI renderer and touches the DOM directly, so it is
// code-split and loaded only in the browser when a reference is on screen.
const Client = lazy(() => import("./Client").then((m) => ({ default: m.RedocClient })));

/**
 * RedocSpec names the bundled OpenAPI document to render: the Map Studio API or
 * the public Tile API.
 */
type RedocSpec = "api" | "tiles";

/**
 * RedocProps configure the {% redoc %} tag. `spec` selects which bundled OpenAPI
 * document under the static descriptions tree to render. `title` and `lead` feed
 * the shared Hero that opens the reference, giving the page the same
 * title-and-overview opening as the rest of the documentation.
 */
interface RedocProps {
  lead?: string;
  spec?: RedocSpec;
  title?: string;
}

/**
 * fallback is the placeholder shown while the reference is server-rendered and
 * during the client load — a centered spinner in a fixed-height box so the column
 * holds a stable footprint while the reference swaps in.
 */
const fallback = (
  <Center h={320}>
    <Loader />
  </Center>
);

/**
 * Redoc is the {% redoc %} renderer the documentation app injects. The page opens with the
 * shared Hero (title and one lead line), like every other page, and the reference sits below
 * it: Redoc's own sidebar, with its search and the specification's sections, tags and
 * operations, beside the reading column.
 *
 * The Hero renders in the static prerender and during the lazy load, above the Suspense
 * boundary, so the title and overview are present immediately while the heavy client loads
 * only in the browser, behind the client-only gate. The reference box owns the vertical scroll,
 * so the Hero scrolls away with the reference and Redoc's sidebar stays in view inside it.
 */
export const Redoc = ({ lead, spec = "tiles", title }: RedocProps) => (
  <div className="redoc-reference">
    {title || lead ? (
      <header className="redoc-header">
        <Hero subtitle={lead} title={title} />
      </header>
    ) : null}
    <ClientOnly fallback={fallback}>
      <Suspense fallback={fallback}>
        <Client specUrl={`/descriptions/openapi/${spec}/index.yaml`} />
      </Suspense>
    </ClientOnly>
  </div>
);
